import * as contractActions from "@rentiohq/shared-frontend/dist/redux/contract/contract.actions";
import * as contractSelectors from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.selectors";
import * as propertyAmenityApi from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.api";
import { IUpsertPropertyAmenityItem } from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.types";
import { IRootStore } from "redux/reducers";
import { EField, IExtraData } from "./schema.requestInsurance.types";

// Amenity name constants
const AMENITY_NAMES = {
  buildingAdjoined: "rentio.default.building_adjoined",
  buildingSurfaceArea: "rentio.default.building_surface_area",
  buildingHasHardMaterials: "rentio.default.building_has_hard_materials",
  buildingFloorCount: "rentio.default.building_floor_count",
} as const;

const createAmenityItem = (
  propertyAmenities: IExtraData["propertyAmenities"],
  name: string,
  value: string | number | boolean | undefined | null,
  dataType: "string" | "number" | "boolean",
): IUpsertPropertyAmenityItem | null => {
  // Check if there's an existing amenity for this name
  const existingAmenity = propertyAmenities?.find(a => a.name === name);

  // If value is undefined and no existing amenity, don't include
  if (value === undefined && !existingAmenity) return null;

  // If value is undefined but there's an existing amenity, send null to delete it
  if (value === undefined && existingAmenity) {
    return {
      id: existingAmenity.id,
      name,
      dataType,
      value: null,
    };
  }

  // If value is null (explicitly cleared) and there's an existing amenity, send null to delete
  if (value === null && existingAmenity) {
    return {
      id: existingAmenity.id,
      name,
      dataType,
      value: null,
    };
  }

  // If value is null/undefined and no existing amenity, don't include
  if (value === null || value === undefined) return null;

  return {
    id: existingAmenity?.id,
    name,
    dataType,
    value,
  };
};

export const isPerformingSubmitActionSelector =
  (contractId: string) => (state: IRootStore) =>
    contractSelectors.isRequestingInsurance(state, contractId);

export const performingSubmitActionErrorSelector =
  (contractId: string) => (state: IRootStore) =>
    contractSelectors.requestInsuranceError(state, contractId);

export const submitActionCreator =
  (extraData: IExtraData) => (formData: any) => {
    const { property, propertyAmenities, contract } = extraData;

    // Build amenity items for upsert to propertyAmenity
    const amenityItems: (IUpsertPropertyAmenityItem | null)[] = [
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.buildingSurfaceArea,
        formData[EField.BuildingSurfaceArea],
        "number",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.buildingHasHardMaterials,
        formData[EField.BuildingHasHardMaterials],
        "boolean",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.buildingAdjoined,
        formData[EField.BuildingAdjoined],
        "string",
      ),
      createAmenityItem(
        propertyAmenities,
        AMENITY_NAMES.buildingFloorCount,
        formData[EField.BuildingLayers],
        "number",
      ),
    ];

    // Filter out null items - only send changed amenities
    const newAmenityItems = amenityItems.filter(
      (item): item is IUpsertPropertyAmenityItem => item !== null,
    );

    // Upsert only the changed amenities
    if (newAmenityItems.length > 0) {
      propertyAmenityApi.upsertForProperty(property.id, {
        amenities: newAmenityItems,
      });
    }

    return contractActions.requestInsurance.actions.start({
      contractId: contract.id,
      insuranceExternalAccessCode: contract.insuranceExternalAccessCode,
      propertyId: property.id,
      updatedProperty: {},
    });
  };
